import React, { useEffect, useState } from "react";
import { api, type BrainExportMode, type BrainExportSection, type BrainRestoreValidateResult, type ConnectorStatusView, type OwnerAuthStatusView, type PermissionItem, type RunnerJobBoundaryView, type SecretRecordView, type WorkerSandboxStatusView } from "../api";
import { SettingsSessionsPanel } from "./SettingsSessionsPanel";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import { ownerConnectorStatusCopy } from "../dashboard/connectorStatusCopy";
import { ROUTINE_CONTROL_ENFORCEMENT_BLOCK, notWiredHint } from "../dashboard/agentStateTaxonomy";

/**
 * IT-SIMO-SHARED-AGENT-ROUTINE-CONTROL-UX-1 — ehrliche Beschriftung der
 * Routine-Control-Felder, die zwar persistiert, aber vom Scheduler/`runNow`
 * noch NICHT ausgewertet werden (approval_policy, quiet_hours,
 * max_runtime_minutes, notification_channels, writes_*). Wirklich wirksam
 * bleiben: Prompt, Trigger-Zeitplan, enable/disable, manueller Testlauf.
 */
const ROUTINE_CONTROL_APPLY_HINT = notWiredHint(ROUTINE_CONTROL_ENFORCEMENT_BLOCK);
const ROUTINE_STUDIO_ONLY = true;
// S2: event/threshold feuert im MVP nach Interval; Connector-Neu-Erkennung und
// Threshold-Vergleich (operator/value) sind noch nicht aktiv.
const ROUTINE_TRIGGER_MVP_HINT =
  "MVP: feuert nach Interval — Connector-/Limit-Pruefung (operator/value) noch nicht aktiv.";

export type SettingsTab = "policy" | "sessions" | "connectors" | "permissions" | "secrets" | "memory" | "routines" | "models";

const TAB_LABELS: Record<SettingsTab, string> = {
  policy: "Policy",
  // SIMO-OWNER-SESSION-MANAGEMENT-UI-1: bewusst weit vorne. Wer wissen will,
  // welche Geraete angemeldet sind, soll nicht suchen muessen.
  sessions: "Geraete",
  connectors: "Connectoren",
  permissions: "Berechtigungen",
  secrets: "Secrets",
  memory: "Memory",
  routines: "Routinen",
  models: "Modelle",
};

export function isSettingsTab(value: string | null | undefined): value is SettingsTab {
  return value != null && Object.prototype.hasOwnProperty.call(TAB_LABELS, value);
}

export function SettingsView({
  initialTab = "policy",
  tabs,
  title = "Einstellungen",
}: {
  initialTab?: SettingsTab;
  tabs?: readonly SettingsTab[];
  title?: string;
} = {}): JSX.Element {
  const [tab, setTab] = useState<SettingsTab>(initialTab);
  const visibleTabs = tabs ?? (Object.keys(TAB_LABELS) as SettingsTab[]);
  return (
    <div className="h-full flex flex-col p-4 sm:p-6 gap-4 sm:gap-6 overflow-hidden">
      <h1 className="text-2xl font-semibold">{title}</h1>
      {visibleTabs.length > 1 && <div className="flex gap-2 border-b border-jarvis-border overflow-x-auto simo-scroll-fade-x">
        {visibleTabs.map((t) => (
          <button
            key={t}
            className={`px-4 py-2 min-h-[44px] inline-flex items-center text-sm border-b-2 transition-colors ${
              tab === t
                ? "border-jarvis-accent text-jarvis-text"
                : "border-transparent text-jarvis-muted hover:text-jarvis-text"
            }`}
            onClick={() => setTab(t)}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>}
      <div className="simo-settings-scroll simo-keyboard-safe-editor flex-1 min-h-0 overflow-auto pr-0 sm:pr-2">
        {tab === "policy" && <PolicyPanel />}
        {tab === "sessions" && <SettingsSessionsPanel />}
        {tab === "connectors" && <ConnectorsPanel />}
        {tab === "permissions" && <PermissionsPanel />}
        {tab === "secrets" && <SecretsPanel />}
        {tab === "memory" && <MemoryPanel />}
        {tab === "routines" && <RoutinesPanel />}
        {tab === "models" && <ModelsPanel />}
      </div>
    </div>
  );
}

interface PolicyOverviewSnapshot {
  ownerAuth: OwnerAuthStatusView | null;
  permissions: PermissionItem[];
  workerSandbox: WorkerSandboxStatusView | null;
  runnerBoundary: RunnerJobBoundaryView | null;
  errors: string[];
  loading: boolean;
}

function policyModeCounts(items: PermissionItem[]): Record<PermissionItem["mode"], number> {
  return items.reduce<Record<PermissionItem["mode"], number>>(
    (acc, item) => ({ ...acc, [item.mode]: acc[item.mode] + 1 }),
    { off: 0, draft: 0, suggest: 0, auto: 0 },
  );
}

function PolicyRow({ label, value, tone = "neutral" }: { label: string; value: string; tone?: "ok" | "warn" | "neutral" }): JSX.Element {
  const toneClass = tone === "ok" ? "text-emerald-300" : tone === "warn" ? "text-amber-300" : "text-jarvis-text";
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-jarvis-border/45 py-2 last:border-0">
      <span className="text-xs text-jarvis-muted">{label}</span>
      <span className={`min-w-0 truncate text-right text-sm font-medium ${toneClass}`}>{value}</span>
    </div>
  );
}

export function PolicyOverviewContent({ snapshot }: { snapshot: PolicyOverviewSnapshot }): JSX.Element {
  const counts = policyModeCounts(snapshot.permissions);
  const workerExecutionOff = snapshot.workerSandbox?.runtimeHardening.execution.enabled === false;
  const runnerGenericAbsent = snapshot.runnerBoundary?.executionEnabled === false && snapshot.runnerBoundary.executeEndpoint == null;
  const safePathGated =
    snapshot.runnerBoundary?.safeExecutionEnabled === true &&
    snapshot.runnerBoundary.ownerAuthRequired &&
    snapshot.runnerBoundary.policyBindingRequired &&
    snapshot.runnerBoundary.workerIdentityRequired;
  const ownerConfigured = snapshot.ownerAuth?.ownerAuth.configured === true;

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-jarvis-accent">Policy Übersicht</h3>
        {snapshot.loading ? (
          <div className="text-sm text-jarvis-muted">Lade Policy-Snapshot…</div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ["Off", counts.off],
              ["Draft", counts.draft],
              ["Suggest", counts.suggest],
              ["Auto", counts.auto],
            ].map(([label, value]) => (
              <div key={label} className="rounded-md border border-jarvis-border bg-jarvis-bg/45 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">{label}</div>
                <div className="kpi text-xl leading-none">{value}</div>
              </div>
            ))}
          </div>
        )}
        {snapshot.errors.length > 0 && (
          <div className="mt-3 rounded-md border border-amber-500/35 bg-amber-950/20 px-3 py-2 text-xs text-amber-100">
            {snapshot.errors.slice(0, 3).map((error) => <div key={error}>{error}</div>)}
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-jarvis-accent">Owner & Secrets</h3>
          <PolicyRow label="Owner Auth" value={ownerConfigured ? "konfiguriert" : "nicht konfiguriert"} tone={ownerConfigured ? "ok" : "warn"} />
          <PolicyRow label="Owner Key" value={snapshot.ownerAuth?.ownerAuth.keyId ?? "-"} />
          <PolicyRow label="Secret-Werte im Renderer" value="blockiert" tone="ok" />
          <PolicyRow label="Vault Boundary" value="Metadaten-only" tone="ok" />
        </div>

        <div className="card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-jarvis-accent">Execution Boundary</h3>
          <PolicyRow label="Worker Execute" value={workerExecutionOff ? "aus" : "pruefen"} tone={workerExecutionOff ? "ok" : "warn"} />
          <PolicyRow label="Runner Execute" value={runnerGenericAbsent ? "nicht vorhanden" : "pruefen"} tone={runnerGenericAbsent ? "ok" : "warn"} />
          <PolicyRow label="Safe Path" value={safePathGated ? "Owner + Policy + Worker-ID" : "pruefen"} tone={safePathGated ? "ok" : "warn"} />
          <PolicyRow label="Shell/Git/Deploy" value="gated" tone="ok" />
        </div>
      </div>

      <div className="card p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-jarvis-accent">Policy Leitplanken</h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="badge bg-jarvis-bg/60">Connector Writes: Approval + Policy</span>
          <span className="badge bg-jarvis-bg/60">Read-only Default</span>
          <span className="badge bg-jarvis-bg/60">Evidence Pflicht</span>
          <span className="badge bg-jarvis-bg/60">Keine Secrets im Renderer</span>
          <span className="badge bg-jarvis-bg/60">Kein Deploy aus Settings</span>
        </div>
      </div>
    </div>
  );
}

function PolicyPanel(): JSX.Element {
  const [snapshot, setSnapshot] = useState<PolicyOverviewSnapshot>({
    ownerAuth: null,
    permissions: [],
    workerSandbox: null,
    runnerBoundary: null,
    errors: [],
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    void Promise.allSettled([
      api.ownerAuthStatus(),
      api.listPermissions(),
      api.workerSandboxStatus(),
      api.runnerJobBoundary(),
    ]).then(([ownerAuth, permissions, workerSandbox, runnerBoundary]) => {
      if (cancelled) return;
      const errors: string[] = [];
      const valueOrNull = <T,>(result: PromiseSettledResult<T>, label: string): T | null => {
        if (result.status === "fulfilled") return result.value;
        errors.push(`${label}: ${result.reason instanceof Error ? result.reason.message : String(result.reason)}`);
        return null;
      };
      const permissionsResult = valueOrNull(permissions, "Permissions");
      setSnapshot({
        ownerAuth: valueOrNull(ownerAuth, "Owner Auth"),
        permissions: permissionsResult?.items ?? [],
        workerSandbox: valueOrNull(workerSandbox, "Worker Sandbox"),
        runnerBoundary: valueOrNull(runnerBoundary, "Runner Boundary"),
        errors,
        loading: false,
      });
    });
    return () => { cancelled = true; };
  }, []);

  return <PolicyOverviewContent snapshot={snapshot} />;
}

export function permissionSettingsErrorCopy(err: unknown): string {
  const text = err instanceof Error ? err.message : String(err);
  if (/permission_denied|available_modes|settings_url|fix_hint|permission_key/i.test(text)) {
    return "Berechtigungs-Aenderung wurde durch die Policy abgelehnt. Bitte erlaubten Modus pruefen und die Aktion erneut setzen.";
  }
  if (/403|forbidden|insufficient_role/i.test(text)) {
    return "Berechtigungs-Aenderung braucht Owner-Rechte. Bitte Owner-Session und Command-Center-Permissions pruefen.";
  }
  if (/invalid.*mode|unsupported.*mode|mode.*not.*allowed/i.test(text)) {
    return "Dieser Berechtigungsmodus ist fuer diese Aktion nicht erlaubt. Bitte einen verfuegbaren Modus waehlen.";
  }
  return ownerFacingGlobalError(text);
}

const PERMISSION_MODE_COPY: Record<PermissionItem["mode"], { label: string; risk: string; effect: string }> = {
  off: {
    label: "Aus",
    risk: "blockiert",
    effect: "SIMO blockiert diese Faehigkeit vollstaendig.",
  },
  draft: {
    label: "Draft",
    risk: "sicherer Entwurf",
    effect: "SIMO darf vorbereiten, aber nichts selbst ausfuehren.",
  },
  suggest: {
    label: "Suggest+Go",
    risk: "Owner-Gate",
    effect: "SIMO darf vorschlagen; kritische Schritte bleiben pruefbar.",
  },
  auto: {
    label: "Auto",
    risk: "hoechste Freigabe",
    effect: "SIMO darf diese Faehigkeit nach Policy automatisch nutzen.",
  },
};

export interface PermissionChangePreview {
  id: string;
  title: string;
  connector: string;
  kind: string;
  from: string;
  to: string;
  risk: string;
  effect: string;
  ownerGate: string;
  audit: string;
  highRisk: boolean;
  changed: boolean;
}

export interface PermissionPolicyDiffReviewPacket {
  schema: "it-simo.permission-policy-diff-review.v1";
  status: "ready_for_owner_confirm" | "unchanged";
  actionId: string;
  connector: string;
  from: string;
  to: string;
  requiredEvidence: string[];
  blockedActions: string[];
  confirmationLabel: string;
}

function isHighRiskPermissionChange(item: PermissionItem, nextMode: PermissionItem["mode"]): boolean {
  if (nextMode !== "auto") return false;
  return /write|delete|execute|deploy|secret|vps|db|shell|git|token|oauth|credential/i.test(`${item.kind} ${item.id} ${item.connector}`);
}

export function buildPermissionChangePreview(item: PermissionItem, nextMode: PermissionItem["mode"]): PermissionChangePreview {
  const next = PERMISSION_MODE_COPY[nextMode];
  const highRisk = isHighRiskPermissionChange(item, nextMode);
  return {
    id: item.id,
    title: item.label,
    connector: item.connector,
    kind: item.kind,
    from: PERMISSION_MODE_COPY[item.mode].label,
    to: next.label,
    risk: highRisk ? "High-Risk Auto" : next.risk,
    effect: highRisk
      ? "SIMO duerfte diese Schreib-/Ausfuehrungsfaehigkeit automatisch nutzen. Nur setzen, wenn Scope, Rollback und Owner-Vertrauen klar sind."
      : next.effect,
    ownerGate: highRisk
      ? "Trust-Root erforderlich; Remote-Requests ohne Owner-Root bleiben serverseitig blockiert."
      : "Aenderung bleibt serverseitig owner-/policy-geprueft.",
    audit: "Jede Aenderung wird als Permission-Audit mit Action, Connector und Modus protokolliert.",
    highRisk,
    changed: item.mode !== nextMode,
  };
}

export function buildPermissionPolicyDiffReviewPacket(
  item: PermissionItem,
  nextMode: PermissionItem["mode"],
): PermissionPolicyDiffReviewPacket {
  const preview = buildPermissionChangePreview(item, nextMode);
  return {
    schema: "it-simo.permission-policy-diff-review.v1",
    status: preview.changed ? "ready_for_owner_confirm" : "unchanged",
    actionId: item.id,
    connector: item.connector,
    from: preview.from,
    to: preview.to,
    requiredEvidence: [
      "Policy-Diff geprueft",
      "Owner-/Trust-Root-Gate verstanden",
      "Audit-Pfad sichtbar",
      preview.highRisk ? "High-Risk-Auto bewusst bestaetigt" : "Moduswechsel owner-lesbar",
    ],
    blockedActions: [
      "no_secret_value_entry",
      "no_renderer_owner_secret",
      "no_deploy_action",
      "no_connector_oauth_start",
      "no_runner_job",
    ],
    confirmationLabel: preview.changed ? "Aenderung anwenden" : "Keine Aenderung",
  };
}

type SettingsConnectorErrorContext =
  | { kind: "load" }
  | { kind: "oauth"; provider: "microsoft" | "google" };

const CONNECTOR_LOAD_ENDPOINT = "/api/connectors";
const OAUTH_START_ENDPOINTS: Record<"microsoft" | "google", string> = {
  microsoft: "/oauth/microsoft/start",
  google: "/oauth/google/start",
};
const OAUTH_PROVIDER_LABELS: Record<"microsoft" | "google", string> = {
  microsoft: "Microsoft",
  google: "Google",
};

function settingsConnectorRawError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function settingsConnectorIssueCopy(text: string, provider?: "microsoft" | "google"): string {
  const providerLabel = provider ? OAUTH_PROVIDER_LABELS[provider] : "Connector";
  if (/invalid_grant|expired or revoked|has been expired or revoked/i.test(text)) {
    return `Connector-Freigabe abgelaufen oder widerrufen. Bitte den betroffenen ${providerLabel}-Connector neu verbinden; bis dahin sind diese Live-Daten nicht belastbar.`;
  }
  if (/401|unauthorized|missing_session|invalid_session|Owner-Session|Owner Auth|API-Zugriff/i.test(text)) {
    return ownerFacingGlobalError(text);
  }
  if (/403|forbidden|insufficient_role/i.test(text)) {
    return "Owner-Freigabe erforderlich. Deine Session ist vorhanden, aber diese Connector-Aktion braucht Owner/Admin-Rechte.";
  }
  if (/client_secret|client_id|redirect_uri|oauth.*config|not_configured|missing.*config|secret/i.test(text)) {
    return `${providerLabel}-OAuth ist serverseitig nicht vollstaendig konfiguriert. Bitte Connector-Secrets und Redirect-URL pruefen; keine Secret-Werte im UI eingeben.`;
  }
  if (/^\s*[\[{]|error_description|traceback|stack trace|authorization|bearer/i.test(text)) {
    return "Connector-Verbindung konnte technisch nicht gestartet werden. Technische Details bleiben serverseitige Evidence; bitte Status erneut pruefen oder Connector neu verbinden.";
  }
  return ownerFacingGlobalError(text);
}

export function settingsConnectorErrorCopy(err: unknown, context: SettingsConnectorErrorContext): string {
  const raw = settingsConnectorRawError(err);
  if (context.kind === "load") {
    return `Connector-Status konnte nicht geladen werden. Endpoint: ${CONNECTOR_LOAD_ENDPOINT}. ${settingsConnectorIssueCopy(raw)}`;
  }
  const providerLabel = OAUTH_PROVIDER_LABELS[context.provider];
  const endpoint = OAUTH_START_ENDPOINTS[context.provider];
  return `${providerLabel}-Verbindung konnte nicht gestartet werden. Endpoint: ${endpoint}. ${settingsConnectorIssueCopy(raw, context.provider)}`;
}

type SettingsRoutineErrorContext =
  | { kind: "load" | "create" }
  | { kind: "update" | "delete" | "run" | "toggle"; id: string };

const ROUTINE_ENDPOINTS = {
  load: "/api/routines",
  create: "/api/routines",
  update: (id: string) => `/api/routines/${encodeURIComponent(id)}`,
  delete: (id: string) => `/api/routines/${encodeURIComponent(id)}`,
  run: (id: string) => `/api/routines/${encodeURIComponent(id)}/run`,
  toggle: (id: string) => `/api/routines/${encodeURIComponent(id)}/toggle`,
};

function settingsRoutineEndpoint(context: SettingsRoutineErrorContext): string {
  switch (context.kind) {
    case "load":
      return ROUTINE_ENDPOINTS.load;
    case "create":
      return ROUTINE_ENDPOINTS.create;
    case "update":
      return ROUTINE_ENDPOINTS.update(context.id);
    case "delete":
      return ROUTINE_ENDPOINTS.delete(context.id);
    case "run":
      return ROUTINE_ENDPOINTS.run(context.id);
    case "toggle":
      return ROUTINE_ENDPOINTS.toggle(context.id);
  }
}

function settingsRoutineActionLabel(context: SettingsRoutineErrorContext): string {
  switch (context.kind) {
    case "load":
      return "Routinen konnten nicht geladen werden";
    case "create":
      return "Routine konnte nicht angelegt werden";
    case "update":
      return "Routine konnte nicht aktualisiert werden";
    case "delete":
      return "Routine konnte nicht geloescht werden";
    case "run":
      return "Routine konnte nicht gestartet werden";
    case "toggle":
      return "Routine-Status konnte nicht geaendert werden";
  }
}

function settingsRoutineIssueCopy(err: unknown): string {
  const text = err instanceof Error ? err.message : String(err);
  if (/401|unauthorized|missing_session|invalid_session|Owner-Session|Owner Auth|API-Zugriff/i.test(text)) {
    return ownerFacingGlobalError(text);
  }
  if (/403|forbidden|insufficient_role|permission_denied/i.test(text)) {
    return "Owner-Freigabe erforderlich. Deine Session ist vorhanden, aber diese Routinen-Aktion braucht Owner/Admin-Rechte oder eine passende Policy.";
  }
  if (/invalid.*trigger|trigger.*invalid|invalid.*routine|routine_invalid|label|required|prompt|required|validation/i.test(text)) {
    return "Routine-Eingaben wurden vom Server abgelehnt. Bitte Label, Prompt und Trigger-Konfiguration pruefen.";
  }
  if (/^\s*[\[{]|error_description|traceback|stack trace|authorization|bearer|fetch failed|networkerror|econnreset|enotfound|etimedout|timeout/i.test(text)) {
    return "Routinen-Aktion konnte technisch nicht belastbar abgeschlossen werden. Technische Details bleiben serverseitige Evidence; bitte Status erneut pruefen.";
  }
  return ownerFacingGlobalError(text);
}

export function settingsRoutineErrorCopy(err: unknown, context: SettingsRoutineErrorContext): string {
  return `${settingsRoutineActionLabel(context)}. Endpoint: ${settingsRoutineEndpoint(context)}. ${settingsRoutineIssueCopy(err)}`;
}

type SettingsDataErrorContext =
  | { area: "secrets"; action: "load" | "delete" }
  | { area: "memory"; action: "load" | "delete" | "backfill" | "export" | "restoreValidate" };

function settingsDataEndpoint(context: SettingsDataErrorContext): string {
  if (context.area === "secrets") {
    return context.action === "delete" ? "/api/secrets/:key" : "/api/secrets";
  }
  if (context.action === "delete") return "/api/memory/:key";
  if (context.action === "backfill") return "/api/memory/backfill-embeddings";
  if (context.action === "export") return "/api/memory/export";
  if (context.action === "restoreValidate") return "/api/memory/restore/validate";
  return "/api/memory";
}

function settingsDataActionLabel(context: SettingsDataErrorContext): string {
  if (context.area === "secrets") {
    return context.action === "delete" ? "Secret konnte nicht geloescht werden" : "Secrets konnten nicht geladen werden";
  }
  switch (context.action) {
    case "load":
      return "Memory konnte nicht geladen werden";
    case "delete":
      return "Memory-Eintrag konnte nicht geloescht werden";
    case "backfill":
      return "Memory-Backfill konnte nicht ausgefuehrt werden";
    case "export":
      return "Memory-Export konnte nicht gestartet werden";
    case "restoreValidate":
      return "Restore-Preflight konnte nicht geprueft werden";
  }
}

function settingsDataIssueCopy(err: unknown): string {
  const text = err instanceof Error ? err.message : String(err);
  if (/401|unauthorized|missing_session|invalid_session|Owner-Session|Owner Auth|API-Zugriff/i.test(text)) {
    return ownerFacingGlobalError(text);
  }
  if (/403|forbidden|insufficient_role|permission_denied/i.test(text)) {
    return "Owner-Freigabe erforderlich. Deine Session ist vorhanden, aber diese Settings-Daten-Aktion braucht Owner/Admin-Rechte oder eine passende Policy.";
  }
  if (/renderer_secret_write_blocked|secret.*write|Credential Manager|vault|secret/i.test(text)) {
    return "Secret-Werte bleiben ausserhalb des Renderer-State. Bitte Vault/Credential-Manager und serverseitige Secret-Boundary pruefen.";
  }
  if (/memory.*not.*found|not_found|unknown.*memory|unknown.*key|invalid.*key|validation|invalid.*export|invalid_restore|restore_count_mismatch|cross_space_restore|confirmFullExport/i.test(text)) {
    return "Settings-Daten-Aktion wurde vom Server abgelehnt. Bitte Auswahl, Export-/Restore-Modus oder den betroffenen Eintrag pruefen.";
  }
  if (/^\s*[\[{]|error_description|traceback|stack trace|authorization|bearer|fetch failed|networkerror|econnreset|enotfound|etimedout|timeout/i.test(text)) {
    return "Settings-Daten-Aktion konnte technisch nicht belastbar abgeschlossen werden. Technische Details bleiben serverseitige Evidence; bitte Status erneut pruefen.";
  }
  return ownerFacingGlobalError(text);
}

export function settingsDataErrorCopy(err: unknown, context: SettingsDataErrorContext): string {
  return `${settingsDataActionLabel(context)}. Endpoint: ${settingsDataEndpoint(context)}. ${settingsDataIssueCopy(err)}`;
}

type OAuthProvider = "microsoft" | "google";

function isMicrosoftConnector(id: string): boolean {
  return id === "outlook" || id === "outlook-cal" || id === "teams";
}

function isGoogleConnector(id: string): boolean {
  return id === "gmail" || id === "gcal";
}

function providerForConnector(id: string): OAuthProvider | null {
  return isMicrosoftConnector(id) ? "microsoft" : isGoogleConnector(id) ? "google" : null;
}

export function SettingsConnectorsContent({
  items,
  connecting,
  error,
  onStartOAuth,
  onCancelConnecting,
  onReload,
  onClearError,
}: {
  items: ConnectorStatusView[];
  connecting: OAuthProvider | null;
  error: string | null;
  onStartOAuth: (provider: OAuthProvider) => void | Promise<void>;
  onCancelConnecting: () => void;
  onReload: () => void;
  onClearError: () => void;
}): JSX.Element {
  return (
    <div className="space-y-4">
      <p className="text-sm text-jarvis-muted">
        Connectoren zeigen nur einen value-freien Status. Wenn OAuth abgelaufen ist,
        fuehrt der naechste Schritt ueber Neu verbinden; Tokens bleiben ausserhalb des Renderers.
      </p>

      {error && (
        <div className="card p-3 border-rose-500/50 bg-rose-500/10 text-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="text-rose-200 whitespace-pre-wrap">{error}</div>
            <button className="btn" onClick={onClearError} aria-label="Fehler schließen">
              ×
            </button>
          </div>
          <button className="btn mt-3" onClick={onReload}>
            Status erneut prüfen
          </button>
        </div>
      )}

      {items.map((connector) => {
        const provider = providerForConnector(connector.id);
        const copy = ownerConnectorStatusCopy(connector);
        const showConnectButton = connector.configured && !connector.reachable && provider !== null;
        const isConnectingThis = provider !== null && connecting === provider;
        const lastSuccess = connector.lastSuccessAt ? new Date(connector.lastSuccessAt).toLocaleString("de-DE") : "nicht gemeldet";
        const riskLabel =
          connector.risk === "owner_action_required"
            ? "Owner-Aktion"
            : connector.risk === "service_degraded"
              ? "Dienst pruefen"
              : copy.risk ?? connector.risk ?? "ok";
        return (
          <div key={connector.id} className="card p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      connector.reachable ? "bg-emerald-400" : connector.configured ? "bg-amber-400" : "bg-rose-400"
                    }`}
                  />
                  <span className="font-semibold">{connector.name}</span>
                  <span className="text-xs font-mono text-jarvis-muted">{connector.id}</span>
                  <span className="badge bg-jarvis-accent/15 text-jarvis-accent">{copy.statusLabel}</span>
                </div>
                <div className="mt-2 text-xs leading-relaxed text-jarvis-muted whitespace-pre-wrap">{copy.message}</div>
                <div className="mt-2 text-xs leading-relaxed text-jarvis-muted/90">Aktion: {copy.action}</div>
                <div className="mt-2 text-xs leading-relaxed text-jarvis-muted/90">Risiko: {riskLabel}</div>
                <div className="mt-2 text-xs leading-relaxed text-jarvis-muted/90">Letzter Erfolg: {lastSuccess}</div>
                <div className="mt-2 text-xs leading-relaxed text-jarvis-muted/90">Evidence: {copy.evidence}</div>
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                {showConnectButton && !isConnectingThis && provider && (
                  <button className="btn-primary" onClick={() => void onStartOAuth(provider)}>
                    Neu verbinden
                  </button>
                )}
                {showConnectButton && isConnectingThis && (
                  <button className="btn" onClick={onCancelConnecting}>
                    Abbrechen
                  </button>
                )}
                <button className="btn" onClick={onReload}>
                  Status prüfen
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ConnectorsPanel(): JSX.Element {
  const [items, setItems] = useState<ConnectorStatusView[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (): void => {
    setLoading(true);
    setError(null);
    void api
      .listConnectors()
      .then((r) => {
        setItems(r.items);
      })
      .catch((e) => {
        setItems([]);
        setError(settingsConnectorErrorCopy(e, { kind: "load" }));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startOAuth = async (provider: "microsoft" | "google"): Promise<void> => {
    setError(null);
    setConnecting(provider);
    let pollHandle: ReturnType<typeof setInterval> | null = null;
    const reset = (): void => {
      if (pollHandle) clearInterval(pollHandle);
      setConnecting(null);
    };
    try {
      const res =
        provider === "microsoft"
          ? await api.microsoftOAuthStart()
          : await api.googleOAuthStart();
      if ("error" in res) {
        setError(settingsConnectorErrorCopy(res.error, { kind: "oauth", provider }));
        reset();
        return;
      }
      const w = window.open(res.url, `${provider}-oauth`, "width=600,height=720");
      if (!w) {
        setError("Pop-Up wurde blockiert. Erlaube Pop-Ups für localhost:5180 und versuche nochmal.");
        reset();
        return;
      }
      const safety = setTimeout(reset, 5 * 60 * 1000);
      pollHandle = setInterval(() => {
        if (w.closed) {
          clearTimeout(safety);
          reset();
          load();
        }
      }, 1000);
    } catch (e) {
      setError(settingsConnectorErrorCopy(e, { kind: "oauth", provider }));
      reset();
    }
  };

  const cancelConnecting = (): void => {
    setConnecting(null);
  };

  const isMicrosoftConnector = (id: string): boolean =>
    id === "outlook" || id === "outlook-cal" || id === "teams";
  const isGoogleConnector = (id: string): boolean =>
    id === "gmail" || id === "gcal";
  const providerFor = (id: string): "microsoft" | "google" | null =>
    isMicrosoftConnector(id) ? "microsoft" : isGoogleConnector(id) ? "google" : null;

  if (loading) return <div className="text-jarvis-muted text-sm">Lade…</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-jarvis-muted">
        Jeder Connector benötigt einen Eintrag im Secrets-Vault, bevor er Daten liefert.
        Microsoft Graph + Google nutzen OAuth — bei <strong>halb-konfiguriert</strong>
        einmal „Verbinden" klicken, im Pop-Up einloggen, fertig.
      </p>

      {error && (
        <div className="card p-3 border-rose-500/50 bg-rose-500/10 text-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="text-rose-200 whitespace-pre-wrap">{error}</div>
            <button
              className="btn"
              onClick={() => setError(null)}
              aria-label="Fehler schließen"
            >
              ×
            </button>
          </div>
          <button className="btn mt-3" onClick={load}>
            Status erneut prüfen
          </button>
        </div>
      )}

      {items.map((c) => {
        const provider = providerFor(c.id);
        const copy = ownerConnectorStatusCopy(c);
        const showConnectButton = c.configured && !c.reachable && provider !== null;
        const isConnectingThis = provider !== null && connecting === provider;
        return (
          <div key={c.id} className="card p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      c.reachable
                        ? "bg-emerald-400"
                        : c.configured
                          ? "bg-amber-400"
                          : "bg-rose-400"
                    }`}
                  />
                  <span className="font-semibold">{c.name}</span>
                  <span className="text-xs font-mono text-jarvis-muted">{c.id}</span>
                  <span
                    className={`badge ${
                      c.reachable
                        ? "bg-emerald-500/20 text-emerald-300"
                        : c.configured
                          ? "bg-amber-500/20 text-amber-300"
                          : "bg-rose-500/20 text-rose-300"
                    }`}
                  >
                    {copy.statusLabel}
                  </span>
                </div>
                <div className="text-xs text-jarvis-muted mt-2 whitespace-pre-wrap leading-relaxed">
                  {copy.message}
                </div>
                <div className="text-xs text-jarvis-muted/90 mt-2 whitespace-pre-wrap leading-relaxed">
                  Fähigkeit: {copy.capability}
                </div>
                <div className="text-xs text-jarvis-muted/90 mt-2 whitespace-pre-wrap leading-relaxed">
                  Aktion: {copy.action}
                </div>
                <div className="text-xs text-jarvis-muted/90 mt-2 whitespace-pre-wrap leading-relaxed">
                  Sichere Alternative: {copy.safeCta}
                </div>
                <div className="text-xs text-jarvis-muted/90 mt-2 whitespace-pre-wrap leading-relaxed">
                  Details: {copy.detailsCta}
                </div>
                <div className="text-xs text-jarvis-muted/90 mt-2 whitespace-pre-wrap leading-relaxed">
                  Owner-Pfad: {copy.ownerPath}
                </div>
                <div className="text-xs text-jarvis-muted/90 mt-2 whitespace-pre-wrap leading-relaxed">
                  Evidence: {copy.evidence}
                </div>
                {copy.risk && (
                  <div className="text-xs text-amber-200/80 mt-2 whitespace-pre-wrap leading-relaxed">
                    Risiko: {copy.risk}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {showConnectButton && !isConnectingThis && provider && (
                  <button
                    className="btn-primary"
                    onClick={() => void startOAuth(provider)}
                  >
                    Verbinden
                  </button>
                )}
                {showConnectButton && isConnectingThis && (
                  <button className="btn" onClick={cancelConnecting}>
                    Abbrechen
                  </button>
                )}
                <button className="btn" onClick={load}>
                  Status prüfen
                </button>
              </div>
            </div>
            {isConnectingThis && (
              <div className="mt-3 pt-3 border-t border-jarvis-border text-xs text-jarvis-muted">
                Pop-Up offen — login bei Microsoft. Bei Abbruch oder Pop-Up-Schließung wird
                der Status automatisch aktualisiert. Falls kein Pop-Up erscheint, schau bei
                Pop-Up-Blockern.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PermissionsPanel(): JSX.Element {
  const [items, setItems] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ item: PermissionItem; mode: PermissionItem["mode"] } | null>(null);

  const load = (): void => {
    setLoading(true);
    void api.listPermissions().then((r) => {
      setItems(r.items);
      setLoading(false);
      setPending(null);
    });
  };
  useEffect(load, []);

  const onChange = async (id: string, mode: PermissionItem["mode"]): Promise<void> => {
    setError(null);
    try {
      await api.setPermission(id, mode);
      load();
    } catch (e) {
      setError(permissionSettingsErrorCopy(e));
    }
  };

  if (loading) return <div className="text-jarvis-muted text-sm">Lade…</div>;

  const pendingPreview = pending ? buildPermissionChangePreview(pending.item, pending.mode) : null;
  const pendingReviewPacket = pending ? buildPermissionPolicyDiffReviewPacket(pending.item, pending.mode) : null;

  const grouped = new Map<string, PermissionItem[]>();
  for (const p of items) {
    const arr = grouped.get(p.connector) ?? [];
    arr.push(p);
    grouped.set(p.connector, arr);
  }

  return (
    <div className="simo-keyboard-safe-editor space-y-6" aria-label="Permission Direct Editor Keyboard-safe Controls">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-jarvis-muted">
          Permission-Änderungen laufen über serverseitig konfigurierte Owner Auth. Der Renderer nimmt keine Owner-Secrets entgegen.
        </p>
      </div>
      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{error}</div>
      )}
      {pendingPreview && (
        <div className="card border-jarvis-accent/45 bg-jarvis-accent/10 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-jarvis-accent">Policy-Diff prüfen</h3>
              <div className="mt-2 text-sm font-medium text-jarvis-text">{pendingPreview.title}</div>
              <div className="mt-1 text-xs text-jarvis-muted">{pendingPreview.connector} · {pendingPreview.id}</div>
              <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
                <div className="rounded-md border border-jarvis-border bg-jarvis-bg/45 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Von</div>
                  <div className="mt-1 font-semibold text-jarvis-text">{pendingPreview.from}</div>
                </div>
                <div className="rounded-md border border-jarvis-border bg-jarvis-bg/45 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Nach</div>
                  <div className="mt-1 font-semibold text-jarvis-text">{pendingPreview.to}</div>
                </div>
                <div className="rounded-md border border-jarvis-border bg-jarvis-bg/45 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Risiko</div>
                  <div className="mt-1 font-semibold text-jarvis-text">{pendingPreview.risk}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-jarvis-muted">{pendingPreview.effect}</div>
              <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
                <div className={`rounded-md border px-3 py-2 ${pendingPreview.highRisk ? "border-amber-400/55 bg-amber-950/25 text-amber-100" : "border-jarvis-border bg-jarvis-bg/35 text-jarvis-muted"}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-75">Owner-Gate</div>
                  <div className="mt-1">{pendingPreview.ownerGate}</div>
                </div>
                <div className="rounded-md border border-jarvis-border bg-jarvis-bg/35 px-3 py-2 text-jarvis-muted">
                  <div className="text-[10px] uppercase tracking-wider opacity-75">Audit</div>
                  <div className="mt-1">{pendingPreview.audit}</div>
                </div>
              </div>
              {pendingReviewPacket && (
                <div className="mt-3 rounded-md border border-jarvis-border bg-jarvis-bg/35 px-3 py-2 text-xs text-jarvis-muted">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-medium text-jarvis-text">Permission Policy Diff Review</div>
                    <div className="badge bg-jarvis-bg/60">{pendingReviewPacket.status}</div>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Required Evidence</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {pendingReviewPacket.requiredEvidence.map((item) => (
                          <span key={item} className="badge bg-jarvis-bg/60">{item}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Blockierte Aktionen</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {pendingReviewPacket.blockedActions.map((item) => (
                          <span key={item} className="badge bg-jarvis-bg/60 text-amber-100">{item}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button className="btn min-h-10" onClick={() => setPending(null)}>
                Abbrechen
              </button>
              <button
                className="btn-primary min-h-10"
                disabled={!pendingPreview.changed}
                onClick={() => pending && void onChange(pending.item.id, pending.mode)}
              >
                Änderung anwenden
              </button>
            </div>
          </div>
        </div>
      )}
      {[...grouped.entries()].map(([connector, list]) => (
        <div key={connector} className="card simo-keyboard-safe-editor p-4">
          <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
            {connector}
          </h3>
          <div className="space-y-2">
            {list.map((item) => (
              <div
                key={item.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{item.label}</div>
                  <div className="text-xs text-jarvis-muted">{item.description}</div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {(["off", "draft", "suggest", "auto"] as const).map((m) => (
                    <button
                      key={m}
                      onClick={() => setPending({ item, mode: m })}
                      className={`inline-flex items-center justify-center min-h-[44px] px-3 rounded text-xs capitalize transition-colors ${
                        item.mode === m
                          ? m === "off"
                            ? "bg-rose-500/30 text-rose-200 border border-rose-500"
                            : m === "draft"
                              ? "bg-amber-500/30 text-amber-200 border border-amber-500"
                              : m === "suggest"
                                ? "bg-blue-500/30 text-blue-200 border border-blue-500"
                                : "bg-emerald-500/30 text-emerald-200 border border-emerald-500"
                          : "bg-jarvis-bg border border-jarvis-border text-jarvis-muted hover:text-jarvis-text"
                      }`}
                      aria-pressed={item.mode === m}
                    >
                      {m === "off" ? "Aus" : m === "suggest" ? "Suggest+Go" : m}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SecretsPanel(): JSX.Element {
  const [items, setItems] = useState<SecretRecordView[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<SecretRecordView | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = (): void => {
    void api
      .listSecrets()
      .then((r) => setItems(r.items))
      .catch((e) => {
        setItems([]);
        showToast(settingsDataErrorCopy(e, { area: "secrets", action: "load" }));
      });
  };
  useEffect(load, []);

  const showToast = (msg: string): void => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const performDelete = async (rec: SecretRecordView): Promise<void> => {
    setBusy(true);
    try {
      await api.removeSecret(rec.key);
      setConfirmDelete(null);
      load();
      showToast(`'${rec.label}' gelöscht`);
    } catch (e) {
      showToast(settingsDataErrorCopy(e, { area: "secrets", action: "delete" }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-4 right-4 z-50 card p-3 text-sm border-jarvis-accent shadow-xl">
          {toast}
        </div>
      )}

      <div className="card p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          Vault Boundary
        </h3>
        <p className="text-xs text-jarvis-muted mt-3">
          Secret-Werte werden nicht im Renderer eingegeben, gehalten oder angezeigt. Die UI zeigt nur Key, Label,
          Connector und Zeitstempel; Rotation erfolgt ausserhalb des Frontend-State.
        </p>
      </div>

      <div className="card p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          Gespeicherte Secrets ({items.length})
        </h3>
        {items.length === 0 ? (
          <div className="text-sm text-jarvis-muted">Noch keine Secrets gespeichert.</div>
        ) : (
          <div className="space-y-1">
            {items.map((s) => (
              <div
                key={s.key}
                className="py-3 border-b border-jarvis-border last:border-0"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.label}</div>
                    <div className="text-xs text-jarvis-muted font-mono truncate">
                      {s.key} {s.connector ? `· ${s.connector}` : ""}
                    </div>
                    <div className="text-xs text-jarvis-muted sm:hidden mt-1">
                      {new Date(s.updatedAt).toLocaleString("de-DE")}
                    </div>
                  </div>
                  <div className="hidden sm:block text-xs text-jarvis-muted whitespace-nowrap">
                    {new Date(s.updatedAt).toLocaleString("de-DE")}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      className="btn-danger"
                      disabled={busy}
                      onClick={() => setConfirmDelete(s)}
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Secret löschen?"
        confirmLabel="Endgültig löschen"
        variant="danger"
        busy={busy}
        busyLabel="Lösche…"
        onConfirm={() => confirmDelete && void performDelete(confirmDelete)}
      >
        <p className="text-sm text-jarvis-muted mb-1">
          <strong>{confirmDelete?.label}</strong>
        </p>
        <p className="text-xs text-jarvis-muted font-mono mb-4">{confirmDelete?.key}</p>
        <p className="text-sm">
          Der Wert wird endgültig aus dem Windows Credential Manager entfernt. Nicht
          rückgängig zu machen.
        </p>
      </ConfirmDialog>
    </div>
  );
}

// ─── Phase 8b: Memory-UI ─────────────────────────────────────────────────

interface MemoryEntry {
  key: string;
  value: string;
  createdAt: string;
  updatedAt: string;
  space: "general" | "private" | "business" | "project" | "people" | "decision" | "evidence" | "temporary";
  kind: "fact" | "preference" | "decision" | "task" | "reflection" | "source" | "context";
  sensitivity: "normal" | "private" | "sensitive" | "secret";
  injectPolicy: "never" | "manual" | "auto_profile";
  retention: "durable" | "temporary";
  reviewStatus: "pending" | "approved" | "rejected";
  source: string | null;
  expiresAt: string | null;
  embeddingModel: string | null;
  embeddingStatus: "current" | "stale" | "missing";
}

interface BackfillResult {
  ok: number;
  failed: number;
  aborted: boolean;
  processed: number;
  pendingRemaining: number;
  model: string;
  errors: Array<{ key: string; error: string }>;
}

type ExportSpace = "all" | MemoryEntry["space"];

const EXPORT_SPACES: ExportSpace[] = ["all", "general", "private", "business", "project", "people", "decision", "evidence", "temporary"];
const EXPORT_SECTIONS: Array<{ id: BrainExportSection; label: string }> = [
  { id: "memory", label: "Memory" },
  { id: "learning_proposals", label: "LearningProposal" },
  { id: "decision_logs", label: "Decision Logs" },
];

function MemoryPanel(): JSX.Element {
  const [items, setItems] = useState<MemoryEntry[]>([]);
  const [currentModel, setCurrentModel] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<MemoryEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [backfillStatus, setBackfillStatus] = useState<BackfillResult | null>(null);
  const [filter, setFilter] = useState("");
  const [exportSpace, setExportSpace] = useState<ExportSpace>("all");
  const [exportMode, setExportMode] = useState<BrainExportMode>("redacted");
  const [exportSections, setExportSections] = useState<BrainExportSection[]>(["memory", "learning_proposals", "decision_logs"]);
  const [fullExportConfirmed, setFullExportConfirmed] = useState(false);
  const [restoreSpace, setRestoreSpace] = useState<ExportSpace>("all");
  const [restoreJson, setRestoreJson] = useState("");
  const [restorePreflight, setRestorePreflight] = useState<BrainRestoreValidateResult | null>(null);

  const load = (): void => {
    setLoading(true);
    void api
      .listMemory()
      .then((r) => {
        setItems(r.entries);
        setCurrentModel(r.currentModel);
      })
      .catch((e) => {
        setItems([]);
        setCurrentModel("");
        showToast(settingsDataErrorCopy(e, { area: "memory", action: "load" }));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const showToast = (msg: string): void => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const performDelete = async (rec: MemoryEntry): Promise<void> => {
    setBusy(true);
    try {
      await api.deleteMemory(rec.key);
      setConfirmDelete(null);
      load();
      showToast(`'${rec.key}' gelöscht`);
    } catch (e) {
      showToast(settingsDataErrorCopy(e, { area: "memory", action: "delete" }));
    } finally {
      setBusy(false);
    }
  };

  const runBackfill = async (): Promise<void> => {
    setBusy(true);
    setBackfillStatus(null);
    try {
      const res = await api.backfillMemoryEmbeddings();
      setBackfillStatus(res);
      load();
      if (res.aborted) {
        showToast(`Backfill abgebrochen — ${res.ok} ok, ${res.failed} failed`);
      } else if (res.processed === 0) {
        showToast("Nichts zu befüllen, alle Embeddings aktuell");
      } else {
        showToast(`Backfill: ${res.ok} ok, ${res.failed} failed, ${res.pendingRemaining} offen`);
      }
    } catch (e) {
      showToast(settingsDataErrorCopy(e, { area: "memory", action: "backfill" }));
    } finally {
      setBusy(false);
    }
  };

  const toggleExportSection = (section: BrainExportSection): void => {
    setExportSections((current) => {
      if (current.includes(section)) {
        const next = current.filter((item) => item !== section);
        return next.length > 0 ? next : current;
      }
      return [...current, section];
    });
  };

  const exportMemory = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.downloadBrainExport({
        space: exportSpace,
        sections: exportSections,
        mode: exportMode,
        confirmFullExport: exportMode === "full" && fullExportConfirmed,
      });
      showToast(exportMode === "full" ? "Full Export gestartet" : "Redacted Export gestartet");
    } catch (e) {
      showToast(settingsDataErrorCopy(e, { area: "memory", action: "export" }));
    } finally {
      setBusy(false);
    }
  };

  const validateRestore = async (): Promise<void> => {
    setBusy(true);
    setRestorePreflight(null);
    try {
      const payload = JSON.parse(restoreJson) as unknown;
      const result = await api.validateBrainRestore(payload, restoreSpace);
      setRestorePreflight(result);
      showToast("Restore-Preflight geprueft, keine Daten wurden geschrieben");
    } catch (e) {
      showToast(settingsDataErrorCopy(e, { area: "memory", action: "restoreValidate" }));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-jarvis-muted text-sm">Lade…</div>;

  const filtered = filter
    ? items.filter(
        (m) =>
          m.key.toLowerCase().includes(filter.toLowerCase()) ||
          m.value.toLowerCase().includes(filter.toLowerCase()),
      )
    : items;

  const counts = {
    current: items.filter((m) => m.embeddingStatus === "current").length,
    stale: items.filter((m) => m.embeddingStatus === "stale").length,
    missing: items.filter((m) => m.embeddingStatus === "missing").length,
  };
  const needsBackfill = counts.stale + counts.missing > 0;

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-4 right-4 z-50 card p-3 text-sm border-jarvis-accent shadow-xl">
          {toast}
        </div>
      )}

      <div className="card p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          Embedding-Status · Aktuelles Modell: <span className="font-mono">{currentModel || "?"}</span>
        </h3>
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="px-3 py-1 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
            {counts.current} aktuell
          </span>
          <span
            className={`px-3 py-1 rounded border ${
              counts.stale > 0
                ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                : "bg-jarvis-bg text-jarvis-muted border-jarvis-border"
            }`}
          >
            {counts.stale} veraltet (anderes Modell)
          </span>
          <span
            className={`px-3 py-1 rounded border ${
              counts.missing > 0
                ? "bg-rose-500/20 text-rose-300 border-rose-500/40"
                : "bg-jarvis-bg text-jarvis-muted border-jarvis-border"
            }`}
          >
            {counts.missing} ohne Embedding
          </span>
        </div>
        <div className="grid grid-cols-1 gap-2 mt-4 sm:flex">
          <button
            className="btn-primary"
            disabled={busy || !needsBackfill}
            onClick={() => void runBackfill()}
          >
            {busy ? "…" : `Backfill starten${needsBackfill ? "" : " (nichts offen)"}`}
          </button>
          <button className="btn" disabled={busy} onClick={load}>
            Neu laden
          </button>
          <button className="btn" disabled={busy || items.length === 0} onClick={exportMemory}>
            JSON-Export
          </button>
        </div>
        {backfillStatus && (
          <div className="mt-3 pt-3 border-t border-jarvis-border text-xs text-jarvis-muted">
            Letzter Backfill: <strong className="text-jarvis-text">{backfillStatus.ok}</strong> ok,{" "}
            <strong className="text-jarvis-text">{backfillStatus.failed}</strong> failed,{" "}
            <strong className="text-jarvis-text">{backfillStatus.pendingRemaining}</strong> offen
            {backfillStatus.aborted ? " (Ollama unreachable → abgebrochen)" : ""}
            {backfillStatus.errors.length > 0 && (
              <ul className="mt-2 space-y-1">
                {backfillStatus.errors.slice(0, 3).map((e) => (
                  <li key={e.key} className="font-mono text-rose-300">
                    {e.key}: {e.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent">
              Brain Exportieren
            </h3>
            <p className="mt-2 max-w-3xl text-xs leading-relaxed text-jarvis-muted">
              Standard ist ein redigierter Export ohne Memory-Werte. Full Export enthält Rohwerte und ist nur für Owner/Admin mit expliziter Bestätigung gedacht.
            </p>
          </div>
          <button
            className={exportMode === "full" ? "btn-danger shrink-0" : "btn-primary shrink-0"}
            disabled={busy || items.length === 0 || (exportMode === "full" && !fullExportConfirmed)}
            onClick={() => void exportMemory()}
          >
            Exportieren
          </button>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-[180px_1fr_260px]">
          <label className="space-y-1 text-xs text-jarvis-muted">
            <span className="uppercase tracking-wider">Space</span>
            <select
              className="input simo-keyboard-safe-field min-h-10 text-base sm:text-sm"
              value={exportSpace}
              onChange={(e) => setExportSpace(e.target.value as ExportSpace)}
            >
              {EXPORT_SPACES.map((space) => (
                <option key={space} value={space}>{space === "all" ? "Alle Spaces" : space}</option>
              ))}
            </select>
          </label>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wider text-jarvis-muted">Bereiche</div>
            <div className="flex flex-wrap gap-2">
              {EXPORT_SECTIONS.map((section) => (
                <label key={section.id} className="inline-flex min-h-[44px] items-center gap-2 rounded border border-jarvis-border bg-jarvis-bg/55 px-3 text-sm">
                  <input
                    type="checkbox"
                    checked={exportSections.includes(section.id)}
                    onChange={() => toggleExportSection(section.id)}
                  />
                  <span>{section.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs uppercase tracking-wider text-jarvis-muted">Modus</div>
            <div className="grid grid-cols-2 gap-1 rounded-md border border-jarvis-border bg-jarvis-bg/55 p-1">
              {(["redacted", "full"] as BrainExportMode[]).map((mode) => (
                <button
                  key={mode}
                  className={`min-h-[40px] rounded px-3 text-sm ${exportMode === mode ? "bg-jarvis-accent/25 text-jarvis-text" : "text-jarvis-muted"}`}
                  onClick={() => {
                    setExportMode(mode);
                    if (mode === "redacted") setFullExportConfirmed(false);
                  }}
                  aria-pressed={exportMode === mode}
                >
                  {mode === "redacted" ? "Redacted" : "Full"}
                </button>
              ))}
            </div>
            {exportMode === "full" && (
              <label className="mt-3 flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-100">
                <input
                  className="mt-0.5"
                  type="checkbox"
                  checked={fullExportConfirmed}
                  onChange={(e) => setFullExportConfirmed(e.target.checked)}
                />
                <span>Ich bestätige den Full Export mit Rohwerten. Nur für kontrollierte Owner/Admin-Sicherung verwenden.</span>
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="card simo-keyboard-safe-editor p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent">
              Restore Preflight
            </h3>
            <p className="mt-2 max-w-3xl text-xs leading-relaxed text-jarvis-muted">
              Export-JSON wird nur validiert: Schema, Space-Grenzen und Zaehler. Es gibt hier keinen Restore-Apply und keine Memory-Mutation.
            </p>
          </div>
          <button
            className="btn-primary shrink-0"
            disabled={busy || restoreJson.trim().length === 0}
            onClick={() => void validateRestore()}
          >
            Preflight pruefen
          </button>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-[180px_1fr]">
          <label className="space-y-1 text-xs text-jarvis-muted">
            <span className="uppercase tracking-wider">Restore Space</span>
            <select
              className="input simo-keyboard-safe-field"
              value={restoreSpace}
              onChange={(e) => setRestoreSpace(e.target.value as ExportSpace)}
            >
              {EXPORT_SPACES.map((space) => (
                <option key={space} value={space}>{space === "all" ? "Alle Spaces" : space}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-xs text-jarvis-muted">
            <span className="uppercase tracking-wider">Export JSON</span>
            <textarea
              className="input simo-keyboard-safe-field min-h-[128px] w-full resize-y font-mono text-xs"
              value={restoreJson}
              onChange={(e) => setRestoreJson(e.target.value)}
              placeholder="simo.brain.export.v1 JSON einfuegen..."
            />
          </label>
        </div>

        {restorePreflight && (
          <div className="mt-4 rounded-md border border-emerald-400/30 bg-emerald-400/10 p-3 text-xs text-emerald-100">
            <div className="font-semibold uppercase tracking-wider">Preflight OK · keine Mutation</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <span>Memory: {restorePreflight.counts.memory}</span>
              <span>Learning: {restorePreflight.counts.learningProposals}</span>
              <span>Decisions: {restorePreflight.counts.decisionLogs}</span>
            </div>
            <div className="mt-2 text-emerald-100/80">{restorePreflight.warning}</div>
            <div className="mt-2 font-mono text-[10px] text-emerald-100/70">Request: {restorePreflight.requestId}</div>
          </div>
        )}
      </div>

      <div className="card simo-keyboard-safe-editor p-4">
        <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent">
            Gespeicherte Memories ({items.length})
          </h3>
          <input
            className="input simo-keyboard-safe-field min-h-10 text-base sm:text-sm"
            placeholder="Filter Key oder Wert…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        {filtered.length === 0 ? (
          <div className="text-sm text-jarvis-muted">
            {items.length === 0
              ? 'Noch keine Memories gespeichert. Speichere mit "memory_save" im Chat.'
              : "Keine Treffer für den Filter."}
          </div>
        ) : (
          <div className="space-y-1">
            {filtered.map((m) => (
              <div
                key={m.key}
                className="py-3 border-b border-jarvis-border last:border-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        m.embeddingStatus === "current"
                          ? "bg-emerald-400"
                          : m.embeddingStatus === "stale"
                            ? "bg-amber-400"
                            : "bg-rose-400"
                      }`}
                      title={
                        m.embeddingStatus === "current"
                          ? `aktuell (${m.embeddingModel})`
                          : m.embeddingStatus === "stale"
                            ? `veraltet (${m.embeddingModel} statt ${currentModel})`
                            : "kein Embedding — Fallback Keyword-Recall"
                      }
                    />
                    <span className="text-sm font-mono truncate">{m.key}</span>
                  </div>
                  <div
                    className="text-xs text-jarvis-muted truncate"
                    title={m.value}
                  >
                    {m.value}
                  </div>
                  <div className="text-[10px] text-jarvis-muted mt-1 font-mono">
                    {new Date(m.updatedAt).toLocaleString("de-DE")}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
                    <span className="rounded border border-jarvis-border bg-jarvis-bg/60 px-1.5 py-0.5 uppercase tracking-wider text-jarvis-muted">
                      {m.space}
                    </span>
                    <span className="rounded border border-jarvis-border bg-jarvis-bg/60 px-1.5 py-0.5 uppercase tracking-wider text-jarvis-muted">
                      {m.kind}
                    </span>
                    <span
                      className={`rounded border px-1.5 py-0.5 uppercase tracking-wider ${
                        m.sensitivity === "normal"
                          ? "border-jarvis-border bg-jarvis-bg/60 text-jarvis-muted"
                          : "border-amber-400/40 bg-amber-400/10 text-amber-200"
                      }`}
                    >
                      {m.sensitivity}
                    </span>
                    <span className="rounded border border-jarvis-border bg-jarvis-bg/60 px-1.5 py-0.5 uppercase tracking-wider text-jarvis-muted">
                      {m.injectPolicy === "auto_profile" ? "auto-profile" : m.injectPolicy}
                    </span>
                    {m.retention === "temporary" && (
                      <span className="rounded border border-rose-400/40 bg-rose-400/10 px-1.5 py-0.5 uppercase tracking-wider text-rose-200">
                        temporary
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn-danger shrink-0 self-start sm:self-auto"
                  disabled={busy}
                  onClick={() => setConfirmDelete(m)}
                >
                  Löschen
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Memory löschen?"
        confirmLabel="Endgültig löschen"
        variant="danger"
        busy={busy}
        busyLabel="Lösche…"
        onConfirm={() => confirmDelete && void performDelete(confirmDelete)}
      >
        <p className="text-xs text-jarvis-muted font-mono mb-2">{confirmDelete?.key}</p>
        <p className="text-sm text-jarvis-muted mb-4 break-words">{confirmDelete?.value}</p>
        <p className="text-sm">
          Der Eintrag wird endgültig gelöscht. Nicht rückgängig zu machen.
        </p>
      </ConfirmDialog>
    </div>
  );
}

// ─── Phase 7: Routinen-UI ─────────────────────────────────────────────────

type TriggerType = "schedule" | "event" | "threshold";
type RoutineApprovalPolicy = "allow" | "ask" | "read_only" | "block" | "session_allow";

interface RoutineItem {
  id: string;
  label: string;
  triggerType: TriggerType;
  triggerConfig: Record<string, unknown>;
  prompt: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface RoutineRun {
  id: number;
  routineId: string;
  triggeredBy: "manual" | "scheduler";
  triggeredAt: string;
  result: "ok" | "error";
  text: string;
}

interface FormState {
  label: string;
  prompt: string;
  triggerType: TriggerType;
  // schedule
  hour: number;
  minute: number;
  weekdays: number[];
  // event/threshold
  connector: "github" | "gmail" | "outlook" | "itsimo";
  kind: "mail" | "pr" | "issue" | "ticket" | "notification";
  // threshold
  operator: ">" | ">=" | "<" | "<=" | "==";
  value: number;
  intervalSec: number;
  timezone: string;
  approvalPolicy: RoutineApprovalPolicy;
  quietHours: string;
  maxRuntimeMinutes: number;
  notificationChannels: string;
  writesBrainProposals: boolean;
  writesRunMemory: boolean;
  writesEvidence: boolean;
}

const EMPTY_FORM: FormState = {
  label: "",
  prompt: "",
  triggerType: "schedule",
  hour: 8,
  minute: 0,
  weekdays: [1, 2, 3, 4, 5],
  connector: "github",
  kind: "pr",
  operator: ">",
  value: 5,
  intervalSec: 300,
  timezone: "Europe/Berlin",
  approvalPolicy: "ask",
  quietHours: "",
  maxRuntimeMinutes: 30,
  notificationChannels: "",
  writesBrainProposals: true,
  writesRunMemory: true,
  writesEvidence: true,
};

function parseRoutineList(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function stringifyRoutineList(value: unknown): string {
  return Array.isArray(value) ? value.filter((part): part is string => typeof part === "string").join(", ") : "";
}

function buildRoutineControlMetadata(f: FormState): Record<string, unknown> {
  return {
    timezone: f.timezone.trim() || "Europe/Berlin",
    approval_policy: f.approvalPolicy,
    quiet_hours: parseRoutineList(f.quietHours),
    max_runtime_minutes: Math.max(0, Math.min(24 * 60, Math.round(f.maxRuntimeMinutes))),
    notification_channels: parseRoutineList(f.notificationChannels),
    writes_brain_proposals: f.writesBrainProposals,
    writes_run_memory: f.writesRunMemory,
    writes_evidence: f.writesEvidence,
  };
}

function buildTriggerConfig(f: FormState): Record<string, unknown> {
  const control = buildRoutineControlMetadata(f);
  if (f.triggerType === "schedule") {
    return {
      type: "schedule",
      hour: f.hour,
      minute: f.minute,
      ...(f.weekdays.length === 7 ? {} : { weekdays: f.weekdays }),
      ...control,
    };
  }
  if (f.triggerType === "event") {
    return {
      type: "event",
      connector: f.connector,
      kind: f.kind,
      interval_min_sec: Math.max(60, f.intervalSec),
      ...control,
    };
  }
  return {
    type: "threshold",
    connector: f.connector,
    kind: f.kind === "notification" ? "ticket" : f.kind,
    operator: f.operator,
    value: f.value,
    interval_min_sec: Math.max(300, f.intervalSec),
    ...control,
  };
}

/**
 * SIMO-JARVIS-LIVING-AGENT-UI-FINAL-1: Ehrliches Aktivierungs-Label pro Routine.
 * Eine aktivierte Routine ist NUR dann wirklich "aktiv/laeuft", wenn der
 * Scheduler serverseitig laeuft (JARVIS_ROUTINES_ENABLED=true). Ist er aus, ist
 * die Routine "geplant" — konfiguriert, aber ohne Auto-Ausfuehrung. Nie
 * "aktiv/laeuft" ohne echten Scheduler-Lauf.
 */
export function deriveRoutineActivationLabel(input: {
  enabled: boolean;
  schedulerEnabled: boolean;
}): { label: string; tone: "active" | "planned" | "paused" } {
  if (!input.enabled) return { label: "pausiert", tone: "paused" };
  if (input.schedulerEnabled) return { label: "aktiv — laeuft nach Zeitplan", tone: "active" };
  return { label: "geplant — Scheduler aus, kein Auto-Lauf", tone: "planned" };
}

export function deriveRoutineNextRunLabel(input: {
  enabled: boolean;
  triggerType: TriggerType;
  triggerConfig: Record<string, unknown>;
  /** Wenn false/undefined: Scheduler laeuft nicht -> nur manueller Testlauf. */
  schedulerEnabled?: boolean;
}, now = new Date()): string {
  if (!input.enabled) return "Naechster Lauf: deaktiviert bis Owner aktiviert";
  if (input.schedulerEnabled === false) return "Nur manueller Testlauf (Scheduler aus)";
  const cfg = input.triggerConfig;
  if (input.triggerType === "event" || input.triggerType === "threshold") {
    const seconds = typeof cfg.interval_min_sec === "number" ? cfg.interval_min_sec : input.triggerType === "threshold" ? 300 : 120;
    const minutes = Math.max(1, Math.round(seconds / 60));
    return `Naechster Poll: spaetestens nach ${minutes} Min.`;
  }
  const hour = typeof cfg.hour === "number" ? cfg.hour : 8;
  const minute = typeof cfg.minute === "number" ? cfg.minute : 0;
  const weekdays = Array.isArray(cfg.weekdays) ? cfg.weekdays.filter((day): day is number => typeof day === "number") : [];
  for (let offset = 0; offset <= 8; offset++) {
    const candidate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + offset, hour, minute, 0, 0));
    if (candidate <= now) continue;
    if (weekdays.length > 0 && !weekdays.includes(candidate.getUTCDay())) continue;
    return `Naechster Lauf: ${candidate.toLocaleString("de-DE", { timeZone: "UTC" })} UTC`;
  }
  return "Naechster Lauf: kein passendes Zeitfenster";
}

export function deriveRoutineLastRunLabel(
  input: { id: string },
  runs: Array<{
    routineId: string;
    triggeredBy: "manual" | "scheduler";
    triggeredAt: string;
    result: "ok" | "error";
  }>,
): string {
  const latest = runs
    .filter((run) => run.routineId === input.id)
    .sort((a, b) => Date.parse(b.triggeredAt) - Date.parse(a.triggeredAt))[0];

  if (!latest) return "Letzter Lauf: noch keiner";

  const when = Number.isFinite(Date.parse(latest.triggeredAt))
    ? new Date(latest.triggeredAt).toLocaleString("de-DE")
    : "Zeit unbekannt";
  const actor = latest.triggeredBy === "manual" ? "manuell" : "scheduler";
  if (latest.result === "error") return `Letzter Fehler: ${when} (${actor})`;
  return `Letzter Lauf: ${when} (${actor}, ok)`;
}

/**
 * Bestehender Routine-Editor (anlegen/bearbeiten/pausieren/loeschen/Testlauf,
 * Zeitplaene, letzte Runs). Exportiert fuer das Agent-Control-Modal
 * (IT-SIMO-SHARED-AGENT-CONTROL-MODAL-1) — gleiche Editor-Logik, kein Duplikat.
 */
export function RoutinesPanel(): JSX.Element {
  const [items, setItems] = useState<RoutineItem[]>([]);
  const [runs, setRuns] = useState<RoutineRun[]>([]);
  const [schedulerEnabled, setSchedulerEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RoutineItem | null>(null);

  const showToast = (m: string): void => {
    setToast(m);
    setTimeout(() => setToast(null), 3500);
  };

  const load = (): void => {
    setLoading(true);
    Promise.all([api.listRoutines(), api.listRoutineRuns(10)])
      .then(([r, runsRes]) => {
        setItems(r.items as RoutineItem[]);
        setRuns(runsRes.items);
        setSchedulerEnabled(r.schedulerEnabled === true);
      })
      .catch((e) => {
        setItems([]);
        setRuns([]);
        setSchedulerEnabled(false);
        showToast(settingsRoutineErrorCopy(e, { kind: "load" }));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (): Promise<void> => {
    if (!form.label.trim() || !form.prompt.trim()) {
      showToast("Label und Prompt sind Pflicht");
      return;
    }
    setBusy(true);
    try {
      const body = {
        label: form.label,
        prompt: form.prompt,
        triggerConfig: buildTriggerConfig(form),
      };
      if (editingId) {
        const res = await api.updateRoutine(editingId, body);
        if ("error" in res) {
          showToast(settingsRoutineErrorCopy(res.error, { kind: "update", id: editingId }));
          return;
        }
        showToast("Aktualisiert");
      } else {
        const res = await api.createRoutine(body);
        if ("error" in res) {
          showToast(settingsRoutineErrorCopy(res.error, { kind: "create" }));
          return;
        }
        showToast("Angelegt");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      load();
    } catch (e) {
      showToast(settingsRoutineErrorCopy(e, editingId ? { kind: "update", id: editingId } : { kind: "create" }));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (r: RoutineItem): void => {
    setEditingId(r.id);
    const cfg = r.triggerConfig;
    setForm({
      label: r.label,
      prompt: r.prompt,
      triggerType: r.triggerType,
      hour: typeof cfg.hour === "number" ? cfg.hour : 8,
      minute: typeof cfg.minute === "number" ? cfg.minute : 0,
      weekdays: Array.isArray(cfg.weekdays) ? (cfg.weekdays as number[]) : [1, 2, 3, 4, 5],
      connector: (cfg.connector as FormState["connector"]) ?? "github",
      kind: (cfg.kind as FormState["kind"]) ?? "pr",
      operator: (cfg.operator as FormState["operator"]) ?? ">",
      value: typeof cfg.value === "number" ? cfg.value : 5,
      intervalSec: typeof cfg.interval_min_sec === "number" ? cfg.interval_min_sec : 300,
      timezone: typeof cfg.timezone === "string" ? cfg.timezone : "Europe/Berlin",
      approvalPolicy: typeof cfg.approval_policy === "string" ? (cfg.approval_policy as RoutineApprovalPolicy) : "ask",
      quietHours: stringifyRoutineList(cfg.quiet_hours),
      maxRuntimeMinutes: typeof cfg.max_runtime_minutes === "number" ? cfg.max_runtime_minutes : 30,
      notificationChannels: stringifyRoutineList(cfg.notification_channels),
      writesBrainProposals: typeof cfg.writes_brain_proposals === "boolean" ? cfg.writes_brain_proposals : true,
      writesRunMemory: typeof cfg.writes_run_memory === "boolean" ? cfg.writes_run_memory : true,
      writesEvidence: typeof cfg.writes_evidence === "boolean" ? cfg.writes_evidence : true,
    });
  };

  const cancelEdit = (): void => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const toggle = async (r: RoutineItem): Promise<void> => {
    setBusy(true);
    try {
      await api.toggleRoutine(r.id, !r.enabled);
      load();
    } catch (e) {
      showToast(settingsRoutineErrorCopy(e, { kind: "toggle", id: r.id }));
    } finally {
      setBusy(false);
    }
  };

  const runNow = async (r: RoutineItem): Promise<void> => {
    setBusy(true);
    try {
      const res = await api.runRoutineNow(r.id);
      if ("error" in res) showToast(settingsRoutineErrorCopy(res.error, { kind: "run", id: r.id }));
      else showToast("Jetzt ausgeführt — siehe Runs unten");
      load();
    } catch (e) {
      showToast(settingsRoutineErrorCopy(e, { kind: "run", id: r.id }));
    } finally {
      setBusy(false);
    }
  };

  const duplicateRoutine = async (r: RoutineItem): Promise<void> => {
    setBusy(true);
    try {
      const res = await api.createRoutine({
        label: `${r.label} (Kopie)`.slice(0, 120),
        prompt: r.prompt,
        triggerConfig: r.triggerConfig,
        enabled: false,
      });
      if ("error" in res) {
        showToast(settingsRoutineErrorCopy(res.error, { kind: "create" }));
        return;
      }
      showToast("Kopie angelegt — deaktiviert bis zur Owner-Prüfung");
      load();
    } catch (e) {
      showToast(settingsRoutineErrorCopy(e, { kind: "create" }));
    } finally {
      setBusy(false);
    }
  };

  const performDelete = async (r: RoutineItem): Promise<void> => {
    setBusy(true);
    try {
      await api.removeRoutine(r.id);
      showToast(`'${r.label}' gelöscht`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      showToast(settingsRoutineErrorCopy(e, { kind: "delete", id: r.id }));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-jarvis-muted text-sm">Lade…</div>;

  return (
    <div className="simo-routines-panel space-y-4 sm:space-y-6 relative">
      {toast && (
        <div className="fixed top-4 right-4 z-50 card p-3 text-sm border-jarvis-accent shadow-xl max-w-sm">
          {toast}
        </div>
      )}

      <div className="card border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100 sm:p-4" data-routine-editor="agent-os-studio-only">
        <strong>Bearbeitung über Agent OS Studio.</strong> Neue Routinen, Änderungen, Aktivieren, Pausieren und Rollback
        laufen ausschließlich unter <span className="font-medium">Agent OS Studio → Routines</span> durch Draft, Validate,
        Simulate, Owner-Freigabe, Apply und Audit. Diese Kompatibilitätsfläche bleibt für Readback und manuelle Testläufe.
      </div>

      {!ROUTINE_STUDIO_ONLY && <div className="card simo-keyboard-safe-editor p-3 sm:p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          {editingId ? "Routine bearbeiten" : "Neue Routine"}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            className="input simo-keyboard-safe-field"
            placeholder="Label (max 120 Zeichen)"
            value={form.label}
            maxLength={120}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <select
            className="input simo-keyboard-safe-field"
            value={form.triggerType}
            onChange={(e) =>
              setForm({ ...form, triggerType: e.target.value as TriggerType })
            }
          >
            <option value="schedule">Schedule — zur Uhrzeit</option>
            <option value="event">Event — Polling nach Interval (MVP, ohne Neu-Erkennung)</option>
            <option value="threshold">Threshold — Polling nach Interval (MVP, ohne Limit-Pruefung)</option>
          </select>

          {form.triggerType === "schedule" && (
            <>
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2 sm:flex">
                <input
                  className="input simo-keyboard-safe-field min-w-0 sm:w-20"
                  type="number"
                  min={0}
                  max={23}
                  value={form.hour}
                  onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })}
                />
                <span className="text-jarvis-muted">:</span>
                <input
                  className="input simo-keyboard-safe-field min-w-0 sm:w-20"
                  type="number"
                  min={0}
                  max={59}
                  value={form.minute}
                  onChange={(e) => setForm({ ...form, minute: Number(e.target.value) })}
                />
                <span className="text-xs text-jarvis-muted">UTC</span>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                {["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"].map((label, i) => (
                  <button
                    key={i}
                    onClick={() =>
                      setForm({
                        ...form,
                        weekdays: form.weekdays.includes(i)
                          ? form.weekdays.filter((d) => d !== i)
                          : [...form.weekdays, i].sort(),
                      })
                    }
                    className={`px-2 py-1 rounded text-xs ${
                      form.weekdays.includes(i)
                        ? "bg-jarvis-accent/30 text-jarvis-text border border-jarvis-accent/50"
                        : "bg-jarvis-bg border border-jarvis-border text-jarvis-muted"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}

          {(form.triggerType === "event" || form.triggerType === "threshold") && (
            <>
              <select
                className="input simo-keyboard-safe-field"
                value={form.connector}
                onChange={(e) =>
                  setForm({ ...form, connector: e.target.value as FormState["connector"] })
                }
              >
                <option value="github">github</option>
                <option value="gmail">gmail</option>
                <option value="outlook">outlook</option>
                <option value="itsimo">itsimo</option>
              </select>
              <select
                className="input simo-keyboard-safe-field"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as FormState["kind"] })}
              >
                <option value="mail">mail</option>
                <option value="pr">pr</option>
                <option value="issue">issue</option>
                <option value="ticket">ticket</option>
                {form.triggerType === "event" && <option value="notification">notification</option>}
              </select>
              {form.triggerType === "threshold" && (
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(88px,0.7fr)] items-center gap-2">
                  <select
                    className="input simo-keyboard-safe-field"
                    value={form.operator}
                    onChange={(e) =>
                      setForm({ ...form, operator: e.target.value as FormState["operator"] })
                    }
                  >
                    <option value=">">&gt;</option>
                    <option value=">=">&ge;</option>
                    <option value="<">&lt;</option>
                    <option value="<=">&le;</option>
                    <option value="==">=</option>
                  </select>
                  <input
                    className="input simo-keyboard-safe-field min-w-0"
                    type="number"
                    min={0}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
                  />
                </div>
              )}
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(96px,0.55fr)] items-center gap-2">
                <span className="min-w-0 text-xs text-jarvis-muted">Poll-Interval (s)</span>
                <input
                  className="input simo-keyboard-safe-field min-w-0"
                  type="number"
                  min={form.triggerType === "threshold" ? 300 : 60}
                  value={form.intervalSec}
                  onChange={(e) => setForm({ ...form, intervalSec: Number(e.target.value) })}
                />
              </div>
              <p
                className="sm:col-span-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-200"
                data-routine-trigger-hint="mvp"
              >
                {ROUTINE_TRIGGER_MVP_HINT} Connector, Art{form.triggerType === "threshold" ? ", Operator und Wert" : ""} werden
                gespeichert und der Prompt entscheidet inhaltlich — die native Neu-Eintrag-/Schwellen-Auswertung folgt als
                Runtime-Block.
              </p>
            </>
          )}

          <div className="sm:col-span-2 grid grid-cols-1 gap-3 rounded-md border border-jarvis-border/60 bg-jarvis-bg/35 p-3 md:grid-cols-2">
            <div
              className="md:col-span-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5 text-[11px] leading-relaxed text-amber-200"
              data-routine-control-hint="not-wired"
            >
              <strong>Routine-Steuerung</strong> (Freigabe-Regel, Quiet Hours, Max. Laufzeit, Benachrichtigungskanaele und die
              Schreib-Schalter): {ROUTINE_CONTROL_APPLY_HINT}. Sie werden gespeichert und angezeigt, aber noch nicht pro Routine
              erzwungen. Wirksam bleiben Prompt, Zeitplan, Aktivieren/Pausieren und der manuelle Testlauf.
            </div>
            <label className="grid gap-1 text-xs text-jarvis-muted">
              Zeitzone
              <input
                className="input simo-keyboard-safe-field"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              />
              <span className="text-[10px] text-jarvis-muted/80">
                Gespeichert; der Zeitplan wird aktuell in UTC ausgewertet.
              </span>
            </label>
            <label className="grid gap-1 text-xs text-jarvis-muted">
              Freigabe-Regel
              <select
                className="input simo-keyboard-safe-field"
                value={form.approvalPolicy}
                onChange={(e) => setForm({ ...form, approvalPolicy: e.target.value as RoutineApprovalPolicy })}
              >
                <option value="ask">fragen</option>
                <option value="allow">erlauben</option>
                <option value="read_only">nur lesen</option>
                <option value="block">blockieren</option>
                <option value="session_allow">Session erlauben</option>
              </select>
            </label>
            <label className="grid gap-1 text-xs text-jarvis-muted">
              Quiet Hours
              <input
                className="input simo-keyboard-safe-field"
                placeholder="22:00-07:00, Wochenende"
                value={form.quietHours}
                onChange={(e) => setForm({ ...form, quietHours: e.target.value })}
              />
            </label>
            <label className="grid gap-1 text-xs text-jarvis-muted">
              Max. Laufzeit (Min.)
              <input
                className="input simo-keyboard-safe-field"
                type="number"
                min={0}
                max={1440}
                value={form.maxRuntimeMinutes}
                onChange={(e) => setForm({ ...form, maxRuntimeMinutes: Number(e.target.value) })}
              />
            </label>
            <label className="grid gap-1 text-xs text-jarvis-muted md:col-span-2">
              Benachrichtigungskanäle
              <input
                className="input simo-keyboard-safe-field"
                placeholder="companion, teams, web-push"
                value={form.notificationChannels}
                onChange={(e) => setForm({ ...form, notificationChannels: e.target.value })}
              />
            </label>
            <div className="grid gap-2 text-xs text-jarvis-muted md:col-span-2 sm:grid-cols-3">
              <span className="sm:col-span-3 flex items-center gap-2 text-[10px] uppercase tracking-wider text-amber-200/80">
                Schreib-Schalter
                <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 normal-case tracking-normal">
                  noch nicht verdrahtet
                </span>
              </span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.writesBrainProposals}
                  onChange={(e) => setForm({ ...form, writesBrainProposals: e.target.checked })}
                />
                Brain-Proposals schreiben
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.writesRunMemory}
                  onChange={(e) => setForm({ ...form, writesRunMemory: e.target.checked })}
                />
                Run Memory schreiben
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.writesEvidence}
                  onChange={(e) => setForm({ ...form, writesEvidence: e.target.checked })}
                />
                Evidence schreiben
              </label>
            </div>
          </div>

          <textarea
            className="input simo-keyboard-safe-field min-h-[112px] resize-y font-mono text-base leading-relaxed sm:col-span-2 sm:text-sm"
            placeholder="Prompt für Jarvis (max 2000 Zeichen). Z.B. ‚Gib mir ein kurzes Briefing für heute‘"
            value={form.prompt}
            maxLength={2000}
            onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          />

          <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:items-center">
            <button className="btn-primary" disabled={busy} onClick={() => void submit()}>
              {busy ? "…" : editingId ? "Speichern" : "Anlegen"}
            </button>
            {editingId && (
              <button className="btn" onClick={cancelEdit}>
                Abbrechen
              </button>
            )}
            <span className="text-xs text-jarvis-muted sm:ml-auto sm:self-center">
              {form.prompt.length}/2000
            </span>
          </div>
        </div>
      </div>}

      <div className="card p-3 sm:p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          Routinen ({items.length})
        </h3>
        <div
          className={`mb-3 rounded-md border px-3 py-2 text-xs ${
            schedulerEnabled
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
              : "border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
          data-scheduler={schedulerEnabled ? "on" : "off"}
        >
          {schedulerEnabled ? (
            <>
              <strong>Scheduler laeuft.</strong> Aktivierte Routinen werden automatisch nach Zeitplan ausgefuehrt.
            </>
          ) : (
            <>
              <strong>Scheduler aus.</strong> Routinen sind konfiguriert/geplant, laufen aber NICHT automatisch
              (<code className="font-mono">JARVIS_ROUTINES_ENABLED</code> beim Start nicht gesetzt). Ein Testlauf per
              <span className="mx-1 font-medium">Jetzt</span> ist trotzdem moeglich.
            </>
          )}
        </div>
        {items.length === 0 ? (
          <div className="text-sm text-jarvis-muted">
            Noch keine Routinen. Lege sie unter <strong>Agent OS Studio → Automation → Routinen</strong> über die genehmigte Change-Lane an.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {items.map((r) => (
              <li
                key={r.id}
                className="py-3 border-b border-jarvis-border last:border-0 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        r.enabled ? "bg-emerald-400" : "bg-jarvis-muted"
                      }`}
                    />
                    <span className="text-sm font-medium truncate">{r.label}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40">
                      {r.triggerType}
                    </span>
                    {(() => {
                      const activation = deriveRoutineActivationLabel({ enabled: r.enabled, schedulerEnabled });
                      const cls =
                        activation.tone === "active"
                          ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                          : activation.tone === "planned"
                            ? "bg-amber-500/20 text-amber-200 border-amber-500/40"
                            : "bg-jarvis-bg/50 text-jarvis-muted border-jarvis-border/60";
                      return (
                        <span
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}
                          data-activation={activation.tone}
                        >
                          {activation.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="text-xs text-jarvis-muted mt-1 truncate" title={r.prompt}>
                    {r.prompt}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider text-jarvis-muted">
                    <span className="rounded border border-jarvis-border/60 bg-jarvis-bg/45 px-1.5 py-0.5">
                      {deriveRoutineNextRunLabel({ ...r, schedulerEnabled })}
                    </span>
                    <span className="rounded border border-jarvis-border/60 bg-jarvis-bg/45 px-1.5 py-0.5">
                      {deriveRoutineLastRunLabel(r, runs)}
                    </span>
                    <span className="rounded border border-jarvis-border/60 bg-jarvis-bg/45 px-1.5 py-0.5">
                      Freigabe: {typeof r.triggerConfig.approval_policy === "string" ? r.triggerConfig.approval_policy : "ask"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap shrink-0">
                  <button className="btn text-xs" disabled={busy} onClick={() => void runNow(r)}>
                    Jetzt
                  </button>
                  {!ROUTINE_STUDIO_ONLY && (
                    <>
                      <button className="btn text-xs" disabled={busy} onClick={() => void toggle(r)}>
                        {r.enabled ? "Pause" : "Aktivieren"}
                      </button>
                      <button className="btn text-xs" disabled={busy} onClick={() => startEdit(r)}>
                        Edit
                      </button>
                      <button className="btn text-xs" disabled={busy} onClick={() => void duplicateRoutine(r)}>
                        Duplizieren
                      </button>
                      <button
                        className="btn-danger text-xs"
                        disabled={busy}
                        onClick={() => setConfirmDelete(r)}
                      >
                        Löschen
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card p-3 sm:p-4">
        <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
          Letzte Runs ({runs.length})
        </h3>
        {runs.length === 0 ? (
          <div className="text-sm text-jarvis-muted">Noch keine Runs.</div>
        ) : (
          <ul className="space-y-1.5">
            {runs.map((run) => (
              <li
                key={run.id}
                className="py-2 border-b border-jarvis-border last:border-0 flex flex-col sm:flex-row sm:items-start sm:gap-4 gap-1"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        run.result === "ok" ? "bg-emerald-400" : "bg-rose-400"
                      }`}
                    />
                    <span className="text-xs font-mono text-jarvis-muted truncate">
                      {run.routineId}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-jarvis-muted">
                      {run.triggeredBy}
                    </span>
                  </div>
                  <div className="text-xs mt-1 truncate" title={run.text}>
                    {run.text}
                  </div>
                </div>
                <div className="text-[10px] text-jarvis-muted whitespace-nowrap">
                  {new Date(run.triggeredAt).toLocaleString("de-DE")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Routine löschen?"
        confirmLabel="Endgültig löschen"
        variant="danger"
        busy={busy}
        busyLabel="Lösche…"
        onConfirm={() => confirmDelete && void performDelete(confirmDelete)}
      >
        <p className="text-sm mb-4">
          <strong>{confirmDelete?.label}</strong> ({confirmDelete?.id})
        </p>
        <p className="text-sm text-jarvis-muted">
          Endgültig, nicht rückgängig zu machen. Vergangene Runs bleiben im
          Audit-Log.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function ModelsPanel(): JSX.Element {
  const [health, setHealth] = useState<Awaited<ReturnType<typeof api.health>> | null>(null);
  useEffect(() => {
    void api.health().then(setHealth);
  }, []);
  return (
    <div className="card p-4">
      <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent mb-3">
        Ollama-Server
      </h3>
      {!health ? (
        <div className="text-sm text-jarvis-muted">Lade…</div>
      ) : (
        <>
          <div className="text-sm">
            <span className={health.ollama.ok ? "text-emerald-400" : "text-rose-400"}>
              {health.ollama.ok ? "Verbunden" : "Nicht erreichbar"}
            </span>
            <span className="ml-2 text-jarvis-muted font-mono">{health.ollama.baseUrl}</span>
          </div>
          <div className="mt-4 text-xs text-jarvis-muted uppercase tracking-wider">
            Installierte Modelle ({health.ollama.models.length})
          </div>
          <ul className="mt-2 space-y-1">
            {health.ollama.models.map((m) => (
              <li key={m} className="text-sm font-mono text-jarvis-text">
                {m}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
